import {Box} from '@mui/material';
import {GridSortItem, GridSortModel} from '@mui/x-data-grid';
import {FC, useState} from 'react';
import {useIntl} from 'react-intl';
import {columnConfigEmployees} from './EmployeesTable.tableConfig';
import {EmployeesTableProps, EmployeeTableSearchParams} from './EmployeesTable.types';
import {EmployeeSortFieldEnum} from 'src/enums';
import {EmployeeItemCard} from './EmployeeItemCard';
import {ResponsiveTable} from '../common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {Table} from '../tables/Table';
import {TableLayout} from '../tables/TableLayout';
import {FilterBar} from '../common/FilterBar';
import {EmployeesTableAction} from './EmployeesTableAction';

export const EmployeesTable: FC<EmployeesTableProps> = ({
                                                            data,
                                                            size,
                                                            pageNumber,
                                                            handleUpdateTableData,
                                                            handleChangePage,
                                                            handleChangeRowsNumber,
                                                            searchValue,
                                                            setSearchValue,
                                                            title,
                                                            onAddClick,
                                                            buttonText,
                                                            onlyTitle,
                                                            icon,
                                                        }) => {
    const intl = useIntl();
    const [sortValue, setSortValue] = useState<GridSortItem>({field: '', sort: 'asc'});

    const handleSearch = (value: string) => {
        setSearchValue(value);
        handleUpdateTableData(updateData(value, sortValue));
    };

    const handleSort = (value: GridSortModel) => {
        setSortValue(value[0]);
        handleUpdateTableData(updateData(searchValue, value[0]));
    };

    /**
     * What this toolbar controls, and only that: the search text and the ordering.
     *
     * There used to be a frozen `{role: '', businessId: 0}` here, a borrowed shape with no control
     * behind it, passed in only to hand `businessId: 0` to a screen that does not read it. The page
     * merges what comes out of here onto the filter its own strip owns.
     */
    const updateData = (search: string, sort: GridSortItem): EmployeeTableSearchParams => ({
        keyword: search,
        businessId: 0,
        sortAsc: sort ? sort.sort === 'asc' : true,
        sortField: (sort && sort.field && Object.values(EmployeeSortFieldEnum).includes(sort.field as EmployeeSortFieldEnum))
            ? sort.field as EmployeeSortFieldEnum
            : EmployeeSortFieldEnum.EMAIL,
    });

    return (
        <Box>
            <TableLayout
                toolbar={(
                    <FilterBar
                        title={title}
                        icon={icon}
                        onAdd={onlyTitle ? undefined : onAddClick}
                        addLabel={buttonText}
                        subtitle={intl.formatMessage({id: 'rowsFound'}, {count: data.page.totalElements})}
                        actions={(
                            <EmployeesTableAction
                                handleSearch={handleSearch}
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                            />
                        )}
                    />
                )}
                totalPages={data.page.totalPages}
                page={pageNumber && pageNumber === 0 ? pageNumber : data.page.number}
                pageSize={size}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangeRowsNumber}
            >
                <ResponsiveTable
                    responsiveElement={(
                        <CardList>
                            {data.content.map((item) => (
                                <EmployeeItemCard
                                    key={item?.id}
                                    {...item}
                                />
                            ))}
                        </CardList>
                    )}
                    desktopElement={
                        <Table
                            rows={data.content}
                            hideFooter
                            columns={columnConfigEmployees()}
                            handleSort={handleSort}
                        />
                    }
                />
            </TableLayout>
        </Box>
    );
};